// Access control for My Dyslexia Tools.
//
// Ways a user gets full access:
//   1. Admin email match (Sarah and team) - bypass paywall entirely.
//   2. Active Stripe subscription stored in Supabase.
//   3. Active school seat (src/lib/school.ts, migration 0004_school_seats.sql).
//   4. School admin (SENCO) - so they can see exactly what their pupils see.
//
// During the 7-day free trial Stripe sets subscription.status='trialing' AND the
// current_period_end is in the future. We treat that as full access.

import type { Session } from '@supabase/supabase-js';
import { supabase } from './supabase';
import { type SchoolSeat, isActiveSchoolSeat } from './school';

// Lower-case for case-insensitive matching.
export const ADMIN_EMAILS = ['info@creativesaucelondon.co.uk'];

// Number of free worksheets a signed-in user gets before the paywall appears.
// Replaces a time-based trial: users get a real, finite slice of value (a
// fortnight of homework, roughly) instead of a clock ticking down. The product
// is marketed as free - no pricing surface in marketing - so by the time a user
// hits the paywall, they have genuinely built a routine around the tool.
// Cost per never-converter at 10 worksheets is roughly $0.40 (mostly vision).
export const FREE_USES = 10;

export function isAdminEmail(email?: string | null): boolean {
  if (!email) return false;
  return ADMIN_EMAILS.includes(email.toLowerCase().trim());
}

export type SubscriptionStatus =
  | 'active'
  | 'trialing'
  | 'past_due'
  | 'canceled'
  | 'incomplete'
  | 'incomplete_expired'
  | 'unpaid'
  | 'inactive';

export interface SubscriptionRow {
  user_id: string;
  status: SubscriptionStatus;
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
  current_period_end: string | null;
  plan: string | null;
}

export async function fetchSubscription(userId: string): Promise<SubscriptionRow | null> {
  const { data, error } = await supabase
    .from('subscriptions')
    .select('user_id, status, stripe_customer_id, stripe_subscription_id, current_period_end, plan')
    .eq('user_id', userId)
    .maybeSingle();
  if (error) return null;
  return (data as SubscriptionRow) ?? null;
}

export function isActiveSubscription(sub: SubscriptionRow | null): boolean {
  if (!sub) return false;
  if (sub.status !== 'active' && sub.status !== 'trialing') return false;
  if (sub.current_period_end && new Date(sub.current_period_end).getTime() < Date.now()) return false;
  return true;
}

/** True if the session's user should have full access to the app.
 *
 * Compliance layer: if the user is under 13 and parental consent is not granted,
 * ALL access is denied regardless of subscription state. This is a hard legal
 * gate (UK GDPR Article 8), not just a UX prompt.
 */
export function hasFullAccess(
  session: Session | null,
  sub: SubscriptionRow | null,
  worksheetsUsed: number,
  profile: UserProfile | null = null,
  schoolSeat: SchoolSeat | null = null,
  isSchoolAdmin = false,
): boolean {
  if (!session?.user) return false;
  // Under-13 without granted parental consent: hard block. Overrides admin,
  // subscription, and free-uses. If Sarah's email is on the admin allow-list
  // she'll never trigger this branch because her profile.age_band will be 18_plus.
  if (profile && profile.age_band === 'under_13' && profile.parent_consent_status !== 'granted') {
    return false;
  }
  if (isAdminEmail(session.user.email)) return true;
  if (isActiveSubscription(sub)) return true;
  if (isActiveSchoolSeat(schoolSeat)) return true;
  if (isSchoolAdmin) return true;
  return worksheetsUsed < FREE_USES;
}

export function freeUsesRemaining(worksheetsUsed: number): number {
  return Math.max(0, FREE_USES - worksheetsUsed);
}

export async function fetchUsage(userId: string): Promise<number> {
  const { data, error } = await supabase
    .from('user_usage')
    .select('worksheets_used')
    .eq('user_id', userId)
    .maybeSingle();
  if (error || !data) return 0;
  return Number((data as { worksheets_used?: number }).worksheets_used ?? 0);
}

/** Atomically increment the signed-in user's worksheet use counter. Returns the new total. */
export async function incrementWorksheetUse(userId: string): Promise<number> {
  const { data, error } = await supabase.rpc('increment_worksheet_use', { uid: userId });
  if (error) {
    // Failure to record the use should not block the user. Return current
    // worksheets_used as a fallback so the UI does not lock them out.
    return await fetchUsage(userId);
  }
  return Number(data ?? 0);
}

// =============================================================================
// User profile - age gate + parental consent + T&Cs acceptance.
//
// Backed by public.user_profiles (migration 0003_age_gate.sql).
//
// Access chain: session -> profile fetched -> hasFullAccess checks:
//   1. is admin? -> full access
//   2. has active subscription or active school seat? -> full access
//   3. under free-use cap? -> access granted (except for under-13 without consent)
//
// Additional gates layered on TOP of the standard access check:
//   - needsAgeCapture       - no DOB recorded yet
//   - needsParentalConsent  - under-13 with parent_consent_status != 'granted'
//   - needsTosAcceptance    - tos_accepted_at null OR tos_version mismatch
// =============================================================================

export type AgeBand = 'under_13' | '13_to_17' | '18_plus';
export type ParentalConsentStatus = 'not_required' | 'pending' | 'granted' | 'revoked';

// Bump this when the customer-facing T&Cs materially change. Users will be
// re-prompted to accept on their next visit.
export const TOS_VERSION = '1';

export interface UserProfile {
  user_id: string;
  date_of_birth: string | null; // ISO date "YYYY-MM-DD"
  age_band: AgeBand | null;
  parent_email: string | null;
  parent_consent_status: ParentalConsentStatus;
  parent_consent_granted_at: string | null;
  tos_accepted_at: string | null;
  tos_version: string | null;
}

export async function fetchUserProfile(userId: string): Promise<UserProfile | null> {
  const { data, error } = await supabase
    .from('user_profiles')
    .select('user_id, date_of_birth, age_band, parent_email, parent_consent_status, parent_consent_granted_at, tos_accepted_at, tos_version')
    .eq('user_id', userId)
    .maybeSingle();
  if (error) return null;
  return (data as UserProfile) ?? null;
}

/** Create the empty profile row if it doesn't exist. Called once after signin. */
export async function ensureUserProfileRow(userId: string): Promise<UserProfile | null> {
  const existing = await fetchUserProfile(userId);
  if (existing) return existing;
  const { error } = await supabase.from('user_profiles').insert({ user_id: userId });
  if (error) return null;
  return await fetchUserProfile(userId);
}

/** Persist DOB. Returns the refreshed profile. The trigger derives age_band + consent status. */
export async function saveDateOfBirth(userId: string, isoDate: string): Promise<UserProfile | null> {
  const { error } = await supabase
    .from('user_profiles')
    .update({ date_of_birth: isoDate })
    .eq('user_id', userId);
  if (error) return null;
  return await fetchUserProfile(userId);
}

export async function saveParentEmail(userId: string, parentEmail: string): Promise<UserProfile | null> {
  const { error } = await supabase
    .from('user_profiles')
    .update({ parent_email: parentEmail.trim().toLowerCase() })
    .eq('user_id', userId);
  if (error) return null;
  return await fetchUserProfile(userId);
}

export async function acceptTos(userId: string): Promise<UserProfile | null> {
  const { error } = await supabase
    .from('user_profiles')
    .update({ tos_accepted_at: new Date().toISOString(), tos_version: TOS_VERSION })
    .eq('user_id', userId);
  if (error) return null;
  return await fetchUserProfile(userId);
}

/** True if the user still needs to enter DOB (no age band set yet). */
export function needsAgeCapture(profile: UserProfile | null): boolean {
  if (!profile) return true;
  return !profile.age_band;
}

/** True if under-13 and consent is not yet granted. Blocks app access. */
export function needsParentalConsent(profile: UserProfile | null): boolean {
  if (!profile) return false;
  if (profile.age_band !== 'under_13') return false;
  return profile.parent_consent_status !== 'granted';
}

/** True if the user hasn't accepted the current T&Cs version. */
export function needsTosAcceptance(profile: UserProfile | null): boolean {
  if (!profile) return true;
  if (!profile.tos_accepted_at) return true;
  return profile.tos_version !== TOS_VERSION;
}
